import type { ContentKey } from '@nvd/claim-shared';
import { useState, type ReactNode } from 'react';
import { usePortal } from '../Portal';
import { UI } from '../lib/ui';
import type { Step } from '../state/machine';
import { Icon } from './Icon';

/**
 * The steps the customer sees: three, or two when the shop doesn't let them choose a resolution.
 * Screens that are not part of the flow show no current step.
 */
const ALL_STEPS: { key: ContentKey; short: ContentKey }[] = [
  { key: 'step.order', short: 'step.order' },
  { key: 'step.problem', short: 'step.problem' },
  { key: 'step.resolution', short: 'step.resolution' },
];

const STEP_OF: Partial<Record<Step, number>> = {
  find: 1,
  otp: 1,
  items: 2,
  issue: 2,
  resolution: 3,
  reorder: 3,
  refund: 3,
  storeCredit: 3,
};

/** `status` is a dead end, so step one is done and nothing is in progress; `success` finishes the flow. */
function progress(step: Step, total: number): { current: number; done: number } {
  if (step === 'success') return { current: 0, done: total };
  if (step === 'status') return { current: 0, done: 1 };
  const current = STEP_OF[step] ?? 0;
  return { current, done: Math.max(0, current - 1) };
}

function Brand({ compact = false }: { compact?: boolean }) {
  const { config } = usePortal();
  const [failed, setFailed] = useState(false);
  const logo = failed ? '' : config.brand.logoUrl;
  const name = config.brand.name || config.shop.replace(/\.myshopify\.com$/, '');

  return (
    <span className="nvd-claim-portal__brand">
      {logo ? (
        <img
          className="nvd-claim-portal__brand-logo"
          src={logo}
          alt={name}
          style={{ height: `${compact ? Math.min(config.brand.logoHeight, 28) : config.brand.logoHeight}px` }}
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="nvd-claim-portal__brand-name">{name}</span>
      )}
    </span>
  );
}

function OrderFooter() {
  const { state, dispatch, t } = usePortal();
  const view = state.view;
  if (!view) return null;

  return (
    <div className="nvd-claim-portal__panel-order">
      <span className="nvd-claim-portal__panel-order-name">{view.order.name}</span>
      {state.email && <span className="nvd-claim-portal__panel-order-email">{state.email}</span>}
      <button type="button" className="nvd-claim-portal__panel-restart" onClick={() => dispatch({ type: 'RESET' })}>
        {t('common.startOver')}
      </button>
    </div>
  );
}

/**
 * The card: a dark panel with the steps on desktop, the same steps as a progress strip on phones, and the
 * current screen beside them.
 */
export function Shell({ children }: { children: ReactNode }) {
  const { config, state, t } = usePortal();
  const steps = config.intake.chooseResolution ? ALL_STEPS : ALL_STEPS.slice(0, 2);
  const { current, done } = progress(state.step, steps.length);
  const intro = state.step === 'find' || state.step === 'otp';

  return (
    <div
      className={`nvd-claim-portal__page nvd-claim-portal__page--${config.theme.layout}${
        intro ? ' nvd-claim-portal__page--intro' : ''
      }`}
    >
      <div className="nvd-claim-portal__frame">
        <aside className="nvd-claim-portal__panel">
          <Brand />
          <h1 className="nvd-claim-portal__panel-title">{t('panel.title')}</h1>
          {intro && <p className="nvd-claim-portal__panel-body">{t('panel.body')}</p>}

          <ol className="nvd-claim-portal__steps">
            {steps.map((step, index) => {
              const number = index + 1;
              const isDone = number <= done;
              const isCurrent = number === current;
              const status = isDone ? 'done' : isCurrent ? 'current' : 'todo';
              return (
                <li
                  key={step.key}
                  className={`nvd-claim-portal__step-item nvd-claim-portal__step-item--${status}`}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  <span className="nvd-claim-portal__step-mark">
                    {isDone ? <Icon name="check" /> : number}
                  </span>
                  {t(step.key)}
                </li>
              );
            })}
          </ol>

          <OrderFooter />
        </aside>

        <div className="nvd-claim-portal__main">
          <header className="nvd-claim-portal__bar">
            <Brand compact />
            {state.view && <span className="nvd-claim-portal__bar-order">{state.view.order.name}</span>}
          </header>

          <ol className="nvd-claim-portal__strip" aria-label={UI.stepsLabel}>
            {steps.map((step, index) => {
              const number = index + 1;
              const status = number <= done ? 'done' : number === current ? 'current' : 'todo';
              return (
                <li
                  key={step.short}
                  className={`nvd-claim-portal__strip-item nvd-claim-portal__strip-item--${status}`}
                  aria-current={number === current ? 'step' : undefined}
                >
                  <span className="nvd-claim-portal__strip-bar" />
                  {number} · {t(step.short)}
                </li>
              );
            })}
          </ol>

          <div className="nvd-claim-portal__screen" aria-live="polite">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
